import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, Icon, InputNumber, KlimRadioGroup, PageSpin, Tooltip } from "components";

type CropIrrigationFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
};

export const CropIrrigationForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropIrrigationFormProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [irrigationActive, setIrrigationActive] = useState<boolean | null>(null);
  const [waterUsedM3, setWaterUsedM3] = useState<number | null>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      irrigationActive,
      waterUsedM3,
    },
    primaryDataId
  );

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setIrrigationActive(cropPrimaryDataQuery.data.irrigationActive ?? null);
      setWaterUsedM3(cropPrimaryDataQuery.data.waterUsedM3 ?? null);

      formRef.current?.setFieldsValue({
        irrigationActive: cropPrimaryDataQuery.data.irrigationActive ?? null,
        waterUsedM3: cropPrimaryDataQuery.data.waterUsedM3 ?? null,
      });

      formRef.current?.validateFields();
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  const isLoading = queryIsLoading(cropPrimaryDataQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropIrrigation.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100 paragraph-m" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropIrrigation.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-crop-irrigation-form" className="padding-100">
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <span>
            {t("cropIrrigation.question.irrigation")}
            {true && <span className="required-asterisk">*</span>}
            <Tooltip title={t("cropIrrigation.tooltips.irrigation")}>
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
            </Tooltip>
          </span>
          <KlimRadioGroup
            required
            name="irrigationActive"
            dataTestId="footprinting-crop-irrigation-radio"
            disabled={readOnly}
            vertical={false}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setIrrigationActive(target.value);
            }}
            value={irrigationActive}
            defaultValue={irrigationActive}
            optionType="button"
            options={[true, false].map((opt) => ({
              label: t(`radioAnswer.${opt}`),
              value: opt,
            }))}
          />
        </Flex>
        <Form ref={formRef}>
          {irrigationActive && (
            <Form.Item className="padding-100">
              <InputNumber
                className="floating-label"
                disabled={readOnly}
                name="waterUsedM3"
                dataTestId="footprinting-crop-water-input"
                label={t("cropIrrigation.question.waterUsedQuestionLabel")}
                tooltip={t("cropIrrigation.tooltips.waterUsedQuestionLabel")}
                suffix="m³"
                value={waterUsedM3}
                initialValue={waterUsedM3}
                onChange={(v) => setWaterUsedM3(v)}
                rules={[
                  { required: true },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                    message: t("cropIrrigation.error.waterUsedLabelError"),
                  },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};
